import { useEffect } from 'react';
import { useCountryCodeLoadable } from '@onramp/state/recoil/selectors/countryCodeSelector';

import { useLogWidgetEventOnce } from './eventing/useLogWidgetEvent';
import { useKillSwitches } from './killswitches/useKillSwitches';
import { checkIfApplePayIsSupported } from './applePay';
import { clientSessionIdStore } from './clientSessionIdStore';
import { isPaymentMethodSupportedByCountry } from './getIsCountryAllowed';

export function useIsApplePayAvailable() {
  const isApplePayKilled = useKillSwitches().kill_cbpay_applepay;

  const logWidgetEvent = useLogWidgetEventOnce();
  const countryLoadable = useCountryCodeLoadable();
  const country = countryLoadable.state === 'hasData' ? countryLoadable.data : undefined;

  // v2 is for US CKO, v1 is for all other countries
  const isApplePayV2AllowedInCountry = isPaymentMethodSupportedByCountry(country, 'apple_pay_v2');

  // if ApplePay isn't supported, we shouldn't track this as exposure to the experiments
  // this is checking for version support & registered debit card
  const isApplePaySupported = checkIfApplePayIsSupported();

  const isIos = clientSessionIdStore.getPlatformAttribution() === 'ios';

  const result = !isApplePayKilled && isApplePaySupported && isApplePayV2AllowedInCountry;

  useEffect(() => {
    const canMakePayments = (() => {
      try {
        const ApplePaySession = typeof window !== 'undefined' ? window.ApplePaySession : undefined;
        if (!ApplePaySession) return false;

        return Boolean(ApplePaySession.canMakePayments());
      } catch {
        return false;
      }
    })();

    const supportsVersion6 = (() => {
      try {
        const ApplePaySession = typeof window !== 'undefined' ? window.ApplePaySession : undefined;
        if (!ApplePaySession) return false;

        return Boolean(ApplePaySession.supportsVersion(6));
      } catch {
        return false;
      }
    })();

    logWidgetEvent('apple_pay_availability_metadata', {
      isIos,
      isApplePaySupported,
      isApplePaySessionDefined: Boolean(window.ApplePaySession),
      isApplePayKilled,
      supportsVersion6,
      canMakePayments,
    });
  }, [isApplePayKilled, isApplePaySupported, isIos, logWidgetEvent, result]);

  return result;
}
